import { PROGRESS_EPOCH } from '~/utils/progressEpoch'
import { apiFetch } from '~/utils/api'

/**
 * 🌟 Rare creature ownership (v1.01, per Javi): rares are bought with
 * Stardust in the title-screen shop and owned FOREVER — meta progress
 * outside run saves, like ascension unlocks and the codex. Runs
 * snapshot the owned list at start (RunState.unlockedCreatures) so a
 * mid-run purchase can't mutate a live draft.
 *
 * 🗂️ v4.93 (audit E6): the ACCOUNT's collection lives server-side in
 * ownership/{uid}, granted only by server-settled purchases and packs.
 * This list is now the display MIRROR: it unions in the server's copy
 * on every wallet refresh, and a one-time import moves the pre-ledger
 * client list up (same amnesty shape as the stardust import).
 */
const STORAGE_KEY = 'elemental-dispatch-owned-creatures'

export function useOwnedCreatures() {
  const owned = useState<string[]>('owned-creatures', () => [])
  const loaded = useState('owned-creatures-loaded', () => false)
  const serverImported = useState('owned-server-imported', () => false)

  /** Load-once guard: own() must never clobber stored purchases. */
  function load() {
    if (loaded.value || typeof localStorage === 'undefined') return
    loaded.value = true
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
      if (Array.isArray(stored)) owned.value = stored.filter(v => typeof v === 'string')
    } catch { /* corrupted storage — start clean */ }
  }

  function persist() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(owned.value))
    }
  }

  function has(defId: string): boolean {
    load()
    return owned.value.includes(defId)
  }

  function own(defId: string) {
    load()
    if (!owned.value.includes(defId)) {
      owned.value = [...owned.value, defId]
      persist()
    }
  }

  /**
   * 🧹 Playtest reset (v2.76, per Javi: "is there a way to reset my
   * creature collection, so i can see it in action?").
   *
   * Goes through the store rather than clearing localStorage by hand on
   * purpose: cloud sync watches `owned` and pushes a full setDoc, so an
   * emptied list REPLACES the cloud copy. Wiping storage directly would
   * be silently undone by the next pull, since adopt() is a union.
   *
   * Caveat that cannot be fixed here: another signed-in device still
   * holding the old list will union it back when it next syncs.
   */
  function reset() {
    loaded.value = true
    owned.value = []
    // 🧹 v5.04: an empty list never re-imports (the import skips empty),
    // but the flag must drop so a future REAL collection can import.
    serverImported.value = false
    persist()
  }

  /** ☁️ Cloud sync (v1.17): union in remotely-owned rares. */
  function adopt(ids: string[]) {
    load()
    const merged = [...new Set([...owned.value, ...ids.filter(v => typeof v === 'string')])]
    if (merged.length !== owned.value.length) {
      owned.value = merged
      persist()
    }
  }

  /** 🗂️ The wallet refresh hands the server collection through here. */
  function noteServer(ids: string[] | null, imported: boolean) {
    if (Array.isArray(ids)) adopt(ids)
    serverImported.value = imported
  }

  /** One-time amnesty: move the pre-ledger client list into the
   * account's server collection. Called after every cloud pull (so the
   * merged picture travels); the server ignores repeats. */
  async function ensureImportedOnServer() {
    if (serverImported.value) return
    load()
    if (owned.value.length === 0) return // nothing to move; a richer device can import later
    const fb = await firebaseReady()
    await fb.auth.authStateReady()
    const user = fb.auth.currentUser
    if (!user) return
    try {
      const token = await user.getIdToken()
      const res = await apiFetch<{ imported: boolean; creatures: string[] }>('/api/ownership/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { creatures: owned.value, epoch: PROGRESS_EPOCH },
      })
      serverImported.value = true
      if (Array.isArray(res.creatures)) adopt(res.creatures)
    } catch (error) {
      console.warn('Collection import unavailable — will retry next sync.', error)
    }
  }

  return { owned, load, has, own, adopt, reset, noteServer, ensureImportedOnServer }
}
